import React from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  /** Optional mark shown in a small chip on the leading edge of the button. */
  icon?: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  as?: React.ElementType;
  href?: string;
  target?: string;
  rel?: string;
  to?: string; // For react-router Link component
  [key: string]: any; // Allow any additional props for polymorphic components
}

/**
 * Leading chip, inspired by the reference UI: a small inset square that carries
 * the icon so the label stays clean and the button reads as one object.
 */
const IconChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5em;
  height: 1.5em;
  margin-left: -0.4em;
  border-radius: ${props => props.theme.borderRadius.sm};
  background: currentColor;
  flex-shrink: 0;

  svg {
    width: 0.9em;
    height: 0.9em;
  }
`;

const ButtonBase = styled(motion.button)<ButtonProps>`
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
  line-height: 1;
  border-radius: ${props => props.theme.borderRadius.minimal};
  cursor: pointer;
  transition:
    background-color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut},
    border-color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut},
    color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[3]};
  text-decoration: none;
  border: 1px solid transparent;
  outline: none;
  white-space: nowrap;

  /* Links nested inside a button inherit the button's own treatment. */
  a {
    color: inherit;
    text-decoration: none;
  }

  ${props => props.fullWidth && css`
    width: 100%;
  `}

  ${props => props.disabled && css`
    opacity: 0.4;
    cursor: not-allowed;
  `}

  /* Size variants */
  ${props => props.size === 'sm' && css`
    padding: ${props.theme.spacing[2]} ${props.theme.spacing[4]};
    font-size: ${props.theme.typography.fontSize.sm};
  `}

  ${props => props.size === 'md' && css`
    padding: ${props.theme.spacing[3]} ${props.theme.spacing[5]};
    font-size: ${props.theme.typography.fontSize.base};
  `}

  ${props => props.size === 'lg' && css`
    padding: ${props.theme.spacing[4]} ${props.theme.spacing[6]};
    font-size: ${props.theme.typography.fontSize.lg};
  `}

  /* Colour variants — green fill, ink outline, or white on dark imagery. */
  ${props => props.variant === 'primary' && css`
    background: ${props.theme.colors.primary[700]};
    color: ${props.theme.colors.white};

    ${IconChip} {
      color: rgba(255, 255, 255, 0.16);

      svg {
        color: ${props.theme.colors.white};
      }
    }

    &:hover:not(:disabled) {
      background: ${props.theme.colors.primary[500]};
    }
  `}

  ${props => props.variant === 'secondary' && css`
    background: transparent;
    color: ${props.theme.colors.ink};
    border-color: ${props.theme.colors.neutral[300]};

    ${IconChip} {
      color: ${props.theme.colors.neutral[200]};

      svg {
        color: ${props.theme.colors.ink};
      }
    }

    &:hover:not(:disabled) {
      border-color: ${props.theme.colors.ink};
      background: ${props.theme.colors.neutral[100]};
    }
  `}

  ${props => props.variant === 'accent' && css`
    background: ${props.theme.colors.white};
    color: ${props.theme.colors.ink};

    ${IconChip} {
      color: ${props.theme.colors.primary[500]};

      svg {
        color: ${props.theme.colors.white};
      }
    }

    &:hover:not(:disabled) {
      background: ${props.theme.colors.primary[500]};
      color: ${props.theme.colors.white};

      ${IconChip} {
        color: rgba(255, 255, 255, 0.2);
      }
    }
  `}

  ${props => props.variant === 'outline' && css`
    background: transparent;
    color: ${props.theme.colors.primary[700]};
    border-color: ${props.theme.colors.primary[700]};

    &:hover:not(:disabled) {
      background: ${props.theme.colors.primary[700]};
      color: ${props.theme.colors.white};
    }
  `}

  ${props => props.variant === 'ghost' && css`
    background: transparent;
    color: ${props.theme.colors.ink};
    border-color: transparent;
    padding-left: 0;
    padding-right: 0;

    &:hover:not(:disabled) {
      color: ${props.theme.colors.primary[500]};
    }
  `}
`;

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  children,
  onClick,
  type = 'button',
  disabled = false,
  as,
  href,
  target,
  rel,
  ...props
}) => {
  return (
    <ButtonBase
      as={as}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      type={as === 'a' ? undefined : type}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      href={href}
      target={target}
      rel={rel}
      whileTap={{ scale: disabled ? 1 : 0.985 }}
      {...props}
    >
      {icon && <IconChip aria-hidden="true">{icon}</IconChip>}
      {children}
    </ButtonBase>
  );
};

export default Button;
